import React, { useState } from 'react';
import styled from '@emotion/styled';
import AlertModal from '~/App/Components/Alerts/AlertModal';
import Button from '~/App/Components/Button';
import { StyleableProps } from '~/App/Components/HOC/Styleable';
import Modal, { ModalProps } from '~/App/Components/Modal';
import VideoUploadConfirmation from '~/App/Modules/MyLibrary/Components/VideoUploadConfirmation';
import VideoLibraryPicker from '~/App/Modules/MediaLibrary/Components/VideoLibraryPicker';
import { Recording } from '~/types/models';
import { useQueryClient } from 'react-query';
import { recordingsQueryKeys } from '~/App/Modules/MyLibrary/Api/Recordings';

interface VideoLibraryPickerModalProps extends Partial<ModalProps> {
  onDeleted: (recording: Recording) => void;
  onClick: (recording: Recording) => void;
  onRequestClose: () => void;
  selectedVideoIds: string[];
}

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const styles: StyleableProps = {
  overlay: ({ base }) => ({
    ...base,
  }),
  panel: ({ base }) => ({
    ...base,
    width: '800px',
    maxWidth: '100%',
    height: '90%',
    maxHeight: '700px',
    padding: '30px 53px 30px 44px',
  }),
  content: ({ base }) => ({
    ...base,
    width: '800px',
    height: '100%',
    maxWidth: '90%',
    margin: '0 auto',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }),
};

interface FileUploadErrorProps {
  title: string;
  msg: string;
}

const VideoLibraryPickerModal = ({
  onRequestClose,
  onDeleted,
  onClick,
  selectedVideoIds,
}: VideoLibraryPickerModalProps) => {
  const [source, setSource] = useState<string>();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<FileUploadErrorProps | null>(null);
  const [activeModal, setActiveModal] = useState('video-library');
  const queryClient = useQueryClient();

  const handleInvalidUploadFile = (title, msg) => {
    setError({ title, msg });
    setActiveModal('error');
  };

  const handleErrorClosed = () => {
    setError(null);
    setActiveModal('video-library');
  };

  const handleValidUploadFile = (file: File) => {
    setFile(file);
    setSource(URL.createObjectURL(file));
    setActiveModal('upload-confirmation');
  };

  const onVideoUploadSuccess = () => {
    queryClient.invalidateQueries(recordingsQueryKeys.all);
    setActiveModal('video-library');
  };

  const modals = {
    error: (
      <AlertModal
        title={error ? error.title : ''}
        message={error ? error.msg : ''}
        onRequestClose={handleErrorClosed}
        isOpen
      />
    ),
    'upload-confirmation': (
      <VideoUploadConfirmation
        isOpen
        videoFile={file as File}
        videoSource={source as string}
        onModalClose={() => setActiveModal('video-library')}
        onVideoUploadSuccess={onVideoUploadSuccess}
      />
    ),
    'video-library': (
      <Modal styles={styles} isOpen hideActions onRequestClose={onRequestClose}>
        <StyledWrapper>
          <VideoLibraryPicker
            selectedVideoIds={selectedVideoIds}
            onDeleted={onDeleted}
            onClick={onClick}
            onValidUploadFile={handleValidUploadFile}
            onInvalidUploadFile={handleInvalidUploadFile}
          />
          <div>
            <Button bsStyle='info' onClick={() => onRequestClose()}>
              Cancel
            </Button>
          </div>
        </StyledWrapper>
      </Modal>
    ),
  };

  return <>{modals[activeModal]}</>;
};

export default VideoLibraryPickerModal;
